/**
 * Environment variable utility that works with both Next.js and Vite
 * Next.js uses process.env, Vite uses import.meta.env
 */

function getEnv(key: string): string | undefined {
  // Check for Vite environment (import.meta.env)
  if (typeof window !== 'undefined' && (window as any).__VITE_ENV__) {
    return (window as any).__VITE_ENV__[key];
  }
  
  // Check for import.meta.env (Vite)
  // Note: This is handled at build time by Vite, so we check for Vite-specific globals
  try {
    const viteEnv = typeof window !== 'undefined' ? (window as any).__VITE_ENV__ : undefined;
    if (viteEnv) {
      return viteEnv[key];
    }
  } catch (e) {
    // Not in Vite environment
  }
  
  // Next.js environment (process.env)
  if (typeof process !== 'undefined' && process.env) {
    return process.env[key];
  }
  
  return undefined;
}

/**
 * Get environment variable with optional default value
 */
export function env(key: string, defaultValue?: string): string {
  return getEnv(key) || defaultValue || '';
}

/**
 * Get boolean environment variable
 */
export function envBool(key: string, defaultValue = false): boolean {
  const value = getEnv(key);
  if (!value) return defaultValue;
  return value === 'true' || value === '1';
}

/**
 * Get number environment variable
 */
export function envNumber(key: string, defaultValue = 0): number {
  const value = getEnv(key);
  if (!value) return defaultValue;
  const parsed = Number(value);
  return isNaN(parsed) ? defaultValue : parsed;
}

// Common environment variables
export const NEXT_PUBLIC_SUPABASE_URL = env('NEXT_PUBLIC_SUPABASE_URL');
export const NEXT_PUBLIC_SUPABASE_ANON_KEY = env('NEXT_PUBLIC_SUPABASE_ANON_KEY');
export const NEXT_PUBLIC_GA_MEASUREMENT_ID = env('NEXT_PUBLIC_GA_MEASUREMENT_ID');
export const NEXT_PUBLIC_APP_URL = env('NEXT_PUBLIC_APP_URL', 'https://bayin.com');
